// eslint-disable @next/next/no-img-element
import React, { FC, useMemo, useState } from "react"
import {
  STable,
  STableColumn,
  SCard,
  SIcon,
  SButton,
  convertTableDataToCsv,
  exportTableDataToCSV,
  STooltip,
} from "@superwall/legacy-ui"
import moment from "moment"
import { abbreviateNumber } from "app/utils/abbreviateNumber"
import _ from "lodash"
import ModalOverlay from "app/core/components/layout/ModalOverlay"
import { Dialog } from "@headlessui/react"
import { useRouter } from "next/router"
import { Routes, useParam } from "@blitzjs/next"
import { PropertyList } from "./ApplicationUser/PropertyList"
import { title } from "process"
import Link from "next/link"
import { EventQueryResponse, EventRow } from "@superwall/clickhouse"
import { SortParams } from "app/experimentGroups/types/experimentGroupReadout/types"
import { SWCountries } from "@superwall/charts"
import { useApplicationContext } from "app/core/contexts/ApplicationContext"

const getCountry = (localeString: string | undefined): string | undefined => {
  if (!localeString) return undefined
  const locale = (localeString.match(/(?<=_)[^_@]+(?=@|$)/) || [])[0]
  return locale
}

const getTableColumns = (
  extraColumns?: STableColumn<EventRow>[],
  userKey?: string
): STableColumn<EventRow>[] => {
  return [
    {
      title: _.startCase(userKey || "User"),
      key: userKey || "app_user_id",
      cellClasses: "!max-w-[300px]",
      headerAlign: "start",
      valueFormatter: (event) => (
        <span className="hover:text-brand-primary">
          <UserCell event={event} userKey={userKey} />
        </span>
      ),
    },
    ...(userKey
      ? [
          {
            title: "User ID",
            key: "app_user_id",
            headerAlign: "start" as const,
            cellClasses: "!max-w-[300px] truncate",
            valueFormatter: (event) => event.app_user_id,
          },
        ]
      : []),
    ...(extraColumns || []),
    {
      title: "When",
      key: "timestamp",
      headerAlign: "start",
      cellClasses: "whitespace-nowrap",
      valueFormatter: (event) =>
        event.timestamp ? moment.utc(event.timestamp).local().fromNow() : "—",
    },
    {
      title: "User Since",
      key: "device_install_timestamp",
      cellClasses: "whitespace-nowrap",
      headerAlign: "start",
      valueFormatter: (event) =>
        event.device_install_timestamp
          ? moment.utc(event.device_install_timestamp).local().format("MMM D, 'YY")
          : "—",
    },
    {
      title: "App Version",
      key: "app_version",
      cellClasses: "whitespace-nowrap",
      headerAlign: "start",
      valueFormatter: (event) => event.app_version,
    },
    {
      title: "SDK Version",
      key: "sdk_version",
      cellClasses: "whitespace-nowrap",
      valueFormatter: (event) => event.sdk_version,
      headerAlign: "start",
    },
    {
      title: "OS Version",
      key: "device_os_version",
      cellClasses: "whitespace-nowrap",
      valueFormatter: (event) => event.device_os_version,
      headerAlign: "start",
    },
    {
      title: "",
      cellClasses: "text-center",
      valueFormatter: (event) => (
        <ModalOverlay
          size="xl"
          // includeX={true}
          render={({ close }) => {
            return (
              <>
                <div className="text-sm text-white pl-1 py-4">
                  <Dialog.Title as="h3" className="text-lg leading-6 text-white pl-8 pb-3">
                    <UserCell event={event} />
                  </Dialog.Title>
                  <SCard
                    header={{ icon: <SIcon type="bolt" size={18} />, title: "Event Properties" }}
                    expanded={false}
                  >
                    <div className="shadow-xl">
                      <PropertyList
                        overflow={false}
                        horizontal={true}
                        keyValues={event.properties}
                      />
                    </div>
                  </SCard>

                  <SCard
                    header={{ icon: <SIcon type="debug" size={18} />, title: "Other Properties" }}
                    expanded={false}
                  >
                    <div className="shadow-xl">
                      <PropertyList
                        overflow={false}
                        horizontal={true}
                        keyValues={_.omit(event, "properties")}
                      />
                    </div>
                  </SCard>
                </div>
              </>
            )
          }}
          renderButton={({ open }) => (
            <SButton
              variant="secondary"
              isMinimal
              className="!px-0 !mx-0 !p-0 !pr-4 text-white opacity-40"
              size="md"
              onClick={(e) => {
                e.stopPropagation()
                open()
              }}
            >
              <SIcon type="info" size={16} />
            </SButton>
          )}
        />
      ),
    },
  ]
}

export const FlagImage: FC<{ localeString?: string; countryCode?: string; className?: string }> = ({
  localeString,
  countryCode,
  className,
}) => {
  const country = countryCode || getCountry(localeString)?.toLowerCase()
  const [error, setError] = useState(false)
  const swCountry = country ? SWCountries[country.toUpperCase()] : undefined

  return country && country.toLowerCase() && !error ? (
    <STooltip placement="top" content={swCountry?.name}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        alt={localeString}
        width={18}
        height={"auto"}
        onError={() => setError(true)}
        className={className}
        src={`/icons/flags/${country.toLowerCase()}.svg`}
      ></img>
    </STooltip>
  ) : (
    <SIcon type="user-properties" className="opacity-50" height={16} />
  )
}

export const UserCell: FC<{ event: EventRow; userKey?: string }> = ({ event, userKey }) => {
  return (
    <span className="flex gap-4 items-center">
      <FlagImage localeString={event.device_locale} className="rounded-sm" />
      <div className="min-w-[150px] truncate grow flex-grow w-full">
        {(userKey ? event.properties[userKey] : event.app_user_id) || (
          <span className="opacity-50">—</span>
        )}
      </div>
    </span>
  )
}

export type EventQueryResponseOptions = {
  isFetching: boolean
  isLoading: boolean
  isRefetching: boolean
  refetch: () => Promise<any>
}

interface UserEventsTableProps {
  response: EventQueryResponse | undefined
  options: EventQueryResponseOptions
  icon?: React.ReactNode
  accessory?: React.ReactNode
  title?: string | React.ReactNode
  subtitle?: string | React.ReactNode
  startsExpanded?: boolean
  exportFullData?: () => Promise<EventQueryResponse | undefined>
}

const sortEventRows = (rows: EventRow[], sortParams: SortParams): EventRow[] => {
  return [...rows].sort((a, b) => {
    const aValue = a[sortParams.key as keyof EventRow]
    const bValue = b[sortParams.key as keyof EventRow]
    if (aValue === bValue) return 0
    if (aValue === undefined) return sortParams.direction === "asc" ? 1 : -1
    if (bValue === undefined) return sortParams.direction === "asc" ? -1 : 1
    if (typeof aValue === "string" && typeof bValue === "string")
      return sortParams.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue)
    return sortParams.direction === "asc"
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number)
  })
}

export const UserEventsTable = ({
  response,
  options,
  icon,
  title,
  accessory,
  subtitle,
  startsExpanded = undefined,
  exportFullData,
}: UserEventsTableProps) => {
  const { enabled } = useApplicationContext()
  const [isExporting, setIsExporting] = useState(false)
  const [sortParams, setSortParams] = useState<SortParams>({
    key: "timestamp",
    direction: "desc",
  })

  const router = useRouter()
  const applicationId = useParam("applicationId", "number")!

  const userKey = useMemo(() => {
    return getMostFrequestUserKey(response?.rows || [])
  }, [response?.rows])

  const columns = useMemo(
    () =>
      getTableColumns(
        [
          ...(response?.columns || []).map((column) => ({
            title: _.startCase(column),
            key: column,
            cellClasses: "truncate",
            headerAlign: "start" as const,
            valueFormatter: (event) => event[column as keyof EventRow] || "",
          })),
        ],
        userKey
      ),
    [response?.columns, userKey]
  )

  const sortedData = useMemo(() => {
    if (!response?.rows) return []
    return sortEventRows(response.rows, sortParams)
  }, [response?.rows, sortParams])

  const csvData = useMemo(
    () => convertTableDataToCsv(columns, response?.rows || []),
    [columns, response?.rows]
  )

  const handleExport = async () => {
    if (!exportFullData) {
      return exportTableDataToCSV(csvData, "user_events.csv")
    }

    setIsExporting(true)
    try {
      const fullData = await exportFullData()
      if (fullData) {
        const fullCsvData = convertTableDataToCsv(columns, fullData.rows || [])

        // Create and download CSV file
        return exportTableDataToCSV(fullCsvData, "user_events.csv")
      } else {
        // Fallback to exporting current data incase fullData is undefined
        return exportTableDataToCSV(csvData, "user_events.csv")
      }
    } catch (error) {
      console.error("Error exporting full data:", error)
    } finally {
      setIsExporting(false)
    }
  }

  const handleColumnClick = (column: STableColumn<EventRow>) => {
    if (column.key) {
      setSortParams((prev) => ({
        key: column.key as string,
        direction: prev.key === column.key ? (prev.direction === "asc" ? "desc" : "asc") : "asc",
      }))
    }
  }

  return (
    <SCard
      expanded={startsExpanded}
      isLoading={options?.isLoading || options?.isFetching || options?.isRefetching}
      header={{
        icon: icon || <SIcon size={18} type="users" />,
        title: title || "Users",
        subtitle: subtitle || `${abbreviateNumber(response?.total || 0)} in the last 24 hours`,
        accessory: (
          <div className="flex flex-row gap-7 items-center justify-center pr-1">
            {accessory}
            <SButton isMinimal isLoading={isExporting} className="!p-0 !m-0" onClick={handleExport}>
              <SIcon size={18} type="download" />
            </SButton>
            <SButton
              isMinimal
              isLoading={options?.isLoading || options?.isFetching || options?.isRefetching}
              className="!p-0 !m-0"
              onClick={async () => {
                await options.refetch()
              }}
            >
              <SIcon size={18} type="refresh" />
            </SButton>
          </div>
        ),
      }}
    >
      <STable
        columns={columns}
        data={sortedData}
        onColumnClick={handleColumnClick}
        sort={sortParams}
        showSortingDirection={true}
        renderRow={(children, row) => {
          if (row.app_user_id) {
            return (
              <Link
                key={`${row.id}`}
                href={
                  enabled("user_page_v2")
                    ? Routes.UserPageV2({
                        applicationId: applicationId,
                        appUserId: row.app_user_id,
                      }).href
                    : Routes.UserPage({ applicationId: applicationId, appUserId: row.app_user_id })
                        .href
                }
                passHref={true}
                target="_blank"
                legacyBehavior
              >
                {children}
              </Link>
            )
          } else {
            return children
          }
        }}
      />
      <div className="h-[1px] bg-white/7 w-full mb-4"></div>
      {/* {response?.debug && (
        <>
          <div className="my-6">
            <SDebugInfo title="Select Query" data={response?.debug.selectQuery} />
            <SDebugInfo title="Count Query" data={response?.debug.countQuery} />
          </div>
        </>
      )} */}
    </SCard>
  )
}

// function that samples user object and return the most frequent getUserKey
export const getMostFrequestUserKey = (users: EventRow[]): string | undefined => {
  let userKeys = users.map((user) => getUserKey(user))
  let keyCount = {}

  userKeys
    .filter((e) => e !== undefined)
    .forEach((key) => {
      keyCount[key!] = (keyCount[key!] || 0) + 1
    })

  const keys = Object.keys(keyCount)

  if (keys.length === 0) {
    return undefined
  }

  return keys.reduce((a, b) => (keyCount[a] > keyCount[b] ? a : b))
}

export const getUserKey = (user: any): string | undefined => {
  const keys = Object.keys(user.properties || user || {})

  const nameKey =
    // keys.find((key) => key.toLowerCase().trim().includes("name")) ||
    keys.find((key) => key.toLowerCase().trim() === "name") ||
    keys.find((key) => key.toLowerCase().trim() === "$name") ||
    keys.find((key) => key.toLowerCase().trim() === "user_name") ||
    keys.find((key) => key.toLowerCase().trim() === "user name") ||
    keys.find((key) => key.toLowerCase().trim() === "username") ||
    keys.find((key) => key.toLowerCase().trim() === "$user_name") ||
    keys.find((key) => key.toLowerCase().trim() === "$user name") ||
    keys.find((key) => key.toLowerCase().trim() === "$username") ||
    keys.find((key) => key.toLowerCase().trim() === "first_name") ||
    keys.find((key) => key.toLowerCase().trim() === "first name") ||
    keys.find((key) => key.toLowerCase().trim() === "firstname") ||
    keys.find((key) => key.toLowerCase().trim() === "$first_name") ||
    keys.find((key) => key.toLowerCase().trim() === "$first name") ||
    keys.find((key) => key.toLowerCase().trim() === "$firstname") ||
    keys.find((key) => key.toLowerCase().trim() === "$lastname") ||
    keys.find((key) => key.toLowerCase().trim() === "full_name") ||
    keys.find((key) => key.toLowerCase().trim() === "full name") ||
    keys.find((key) => key.toLowerCase().trim() === "fullname") ||
    keys.find((key) => key.toLowerCase().trim() === "$full_name") ||
    keys.find((key) => key.toLowerCase().trim() === "$full name") ||
    keys.find((key) => key.toLowerCase().trim() === "$fullname") ||
    keys.find((key) => key.toLowerCase().trim() === "display_name") ||
    keys.find((key) => key.toLowerCase().trim() === "display name") ||
    keys.find((key) => key.toLowerCase().trim() === "displayname") ||
    keys.find((key) => key.toLowerCase().trim() === "$display_name") ||
    keys.find((key) => key.toLowerCase().trim() === "$display name") ||
    keys.find((key) => key.toLowerCase().trim() === "$displayname") ||
    keys.find((key) => key.toLowerCase().trim() === "email") ||
    keys.find((key) => key.toLowerCase().trim() === "e-mail") ||
    keys.find((key) => key.toLowerCase().trim() === "e mail") ||
    keys.find((key) => key.toLowerCase().trim() === "e_mail") ||
    keys.find((key) => key.toLowerCase().trim() === "$email") ||
    keys.find((key) => key.toLowerCase().trim().endsWith("email")) ||
    keys.find((key) => key.toLowerCase().trim() === "last_name") ||
    keys.find((key) => key.toLowerCase().trim() === "last name") ||
    keys.find((key) => key.toLowerCase().trim() === "lastname") ||
    keys.find((key) => key.toLowerCase().trim() === "$last_name") ||
    keys.find((key) => key.toLowerCase().trim() === "$last name")

  return nameKey
}
